import React from 'react';
import { useTranslation } from 'react-i18next';
import { fmtNum } from '../../lib/formatHelpers';

/**
 * V2 CreatorDetailHero — Carte d'en-tête du détail créateur.
 * Avatar + identité + KPIs synthétiques (tier, server, status, manager).
 *
 * Props :
 *  - creator       : creators row (+ manager_name)
 *  - currentMonth  : creator_current_month row (tier_current, etc.)
 *  - basePath      : '/admin' or '/manager' — for back link
 *  - role          : 'admin' | 'manager'
 *  - onBack        : optional callback
 */
const Stat = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold truncate">{label}</div>
    <div className="text-base md:text-lg font-heading font-semibold text-foreground mt-0.5 truncate">{value}</div>
  </div>
);

const CreatorDetailHero = ({ creator, currentMonth, role = 'admin' }) => {
  const { t } = useTranslation();
  if (!creator) return null;
  const tier = currentMonth?.tier_current ?? creator.tier;
  const tierLM = currentMonth?.tier_last_month;
  const daysJoined = currentMonth?.days_since_joining;

  return (
    <section className="rounded-2xl bg-card border border-border overflow-hidden">
      <div className="px-5 md:px-6 py-5 md:py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        {/* Avatar */}
        <div className="shrink-0">
          {creator.avatar_url ? (
            <img
              src={creator.avatar_url}
              alt={creator.display_name || creator.tiktok_username}
              className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover border border-border"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-muted flex items-center justify-center text-2xl font-heading font-bold text-muted-foreground border border-border">
              {(creator.display_name || creator.tiktok_username || '?').charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Identity */}
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground truncate">
            {creator.display_name || creator.tiktok_username || '—'}
          </h2>
          {creator.tiktok_username && (
            <div className="text-sm text-muted-foreground font-data mt-0.5 truncate">@{creator.tiktok_username}</div>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-muted text-foreground font-data">
              T{tier ?? '—'}{tierLM != null && tierLM !== tier ? <span className="text-muted-foreground"> (T{tierLM} M-1)</span> : null}
            </span>
            {creator.server && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-primary/10 text-primary">
                {creator.server}
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
              creator.status === 'active'    ? 'bg-success/10 text-success'
            : creator.status === 'inactive'  ? 'bg-muted text-muted-foreground'
            : creator.status === 'suspended' ? 'bg-warning/10 text-warning'
            : 'bg-muted text-muted-foreground'
            }`}>
              {t(`creatorsV2.status.${creator.status || 'unknown'}`, creator.status || '—')}
            </span>
          </div>
        </div>

        {/* Stats column (right) */}
        <div className="grid grid-cols-3 gap-3 md:gap-5 md:max-w-md w-full md:w-auto">
          <Stat
            label={t('creatorsV2.detail.daysJoined', 'Days since joining')}
            value={daysJoined != null ? fmtNum(daysJoined) : '—'}
          />
          <Stat
            label={t('creatorsV2.detail.validDays', 'Valid days')}
            value={fmtNum(creator.valid_days)}
          />
          <Stat
            label={t('creatorsV2.detail.manager', 'Manager')}
            value={creator.manager_name || '—'}
          />
        </div>
      </div>

      {creator.whatsapp_phone && role !== 'creator' && (
        <div className="px-5 md:px-6 py-3 border-t border-border bg-surface-2 flex items-center justify-between gap-3">
          <div className="text-xs">
            <span className="text-muted-foreground">{t('creatorsV2.detail.whatsapp', 'WhatsApp')}</span>{' '}
            <span className="font-data text-foreground">{creator.whatsapp_phone}</span>
          </div>
          <a
            href={`https://wa.me/${creator.whatsapp_phone.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1 rounded border border-border hover:bg-muted text-foreground"
          >
            {t('creatorsV2.detail.contact', 'Contact')} →
          </a>
        </div>
      )}
    </section>
  );
};

export default CreatorDetailHero;
